import { ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DimePopover, DimePopoverTrigger } from '../DimePopover.js';
import { DimeTooltip } from '../DimeTooltip.js';
import { useDisabledTooltip } from '../lib/use-disabled-tooltip.js';
import { cn } from '../lib/utils.js';
import type { DimeTreeComboboxNode } from './tree-combobox-model.js';
import { TreeComboboxPanel } from './TreeComboboxPanel.js';
import { TreeComboboxPopoverContent } from './TreeComboboxPopoverContent.js';

export type DimeTreeComboboxProps = {
  roots: readonly DimeTreeComboboxNode[];
  /** Minimal set of explicitly checked ids; descendants of a checked id are implied. */
  value: readonly string[];
  onValueChange: (value: string[]) => void;
  triggerLabel: string;
  /** Shown on hover of the trigger, one entry per line. */
  triggerTooltip?: string;
  /** Why the trigger is disabled; shown on hover, keyboard focus or tap, and announced as its description. */
  disabledTooltip?: string;
  ariaLabel: string;
  searchPlaceholder?: string;
  emptyMessage?: (query: string) => string;
  renderTrailing?: (node: DimeTreeComboboxNode) => React.ReactNode;
  isMuted?: (node: DimeTreeComboboxNode) => boolean;
  clearLabel?: string;
  disabled?: boolean;
  /** `sm` matches the compact filter controls a toolbar sits it beside. */
  size?: 'default' | 'sm';
  /** Which end of an overflowing trigger label gets the ellipsis; 'start' keeps the last path segment visible. */
  truncateLabel?: 'end' | 'start';
  /** Applied to the wrapper; sets the trigger's width (default `w-44`). */
  className?: string;
};

const defaultEmptyMessage = (query: string) => `No categories match “${query}”`;

/** Multi-select tree picker: a search input and an expandable checkbox tree in a popover. */
export function DimeTreeCombobox({
  roots,
  value,
  onValueChange,
  triggerLabel,
  triggerTooltip,
  disabledTooltip,
  ariaLabel,
  searchPlaceholder = 'Search categories',
  emptyMessage = defaultEmptyMessage,
  renderTrailing,
  isMuted,
  clearLabel = 'Clear categories',
  disabled = false,
  size = 'default',
  truncateLabel = 'end',
  className,
}: Readonly<DimeTreeComboboxProps>) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const hasValue = value.length > 0;

  // The clear button unmounts once the value empties, so focus moves to the trigger first.
  const handleClear = () => {
    onValueChange([]);
    triggerRef.current?.focus();
  };

  const trigger = (
    <DimePopoverTrigger asChild>
      <DimeButton
        ref={triggerRef}
        type="button"
        variant="outline"
        size={size}
        disabled={disabled}
        className={cn('w-full min-w-0 justify-between text-left font-normal', hasValue && 'pr-8')}
      >
        {truncateLabel === 'start' ? (
          // WHY: rtl moves the ellipsis to the left edge, and the bdi keeps the LTR text and " › " in reading order.
          <span dir="rtl" className="truncate text-left">
            <bdi>{triggerLabel}</bdi>
          </span>
        ) : (
          <span className="truncate">{triggerLabel}</span>
        )}
        {!hasValue && (
          <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        )}
      </DimeButton>
    </DimePopoverTrigger>
  );
  const explainedTrigger = useDisabledTooltip(
    { disabled, tooltip: disabledTooltip, fullWidth: true },
    trigger,
  );
  // A disabled trigger takes no hover, so the disabled-reason wrapper replaces the hover tooltip.
  // The key remounts on that swap, since the disabled tooltip's open state is controlled and this one's is not.
  const triggerWithTooltip =
    triggerTooltip && !disabled ? (
      <DimeTooltip
        key="trigger-tooltip"
        content={<span className="whitespace-pre-line">{triggerTooltip}</span>}
      >
        {trigger}
      </DimeTooltip>
    ) : (
      explainedTrigger
    );

  return (
    <div className={cn('relative w-44 shrink-0', className)}>
      <DimePopover open={open} onOpenChange={setOpen}>
        {triggerWithTooltip}
        <TreeComboboxPopoverContent
          align="start"
          aria-label={ariaLabel}
          className="w-72 gap-0 p-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <TreeComboboxPanel
            roots={roots}
            value={value}
            onValueChange={onValueChange}
            ariaLabel={ariaLabel}
            searchPlaceholder={searchPlaceholder}
            emptyMessage={emptyMessage}
            renderTrailing={renderTrailing}
            isMuted={isMuted}
            inputRef={inputRef}
          />
        </TreeComboboxPopoverContent>
      </DimePopover>
      {hasValue && (
        <DimeButton
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={disabled}
          aria-label={clearLabel}
          className="absolute top-1/2 right-1.5 z-10 -translate-y-1/2"
          onClick={handleClear}
        >
          <XIcon aria-hidden="true" />
        </DimeButton>
      )}
    </div>
  );
}
